import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { fyEnd, fyOf, fyOptions, fyStart, thisMonth } from "@/components/monitoring/shared";
import PortfolioTab from "@/components/monitoring/PortfolioTab";
import ProjectView from "@/components/monitoring/ProjectView";
import IndicatorsTable from "@/components/monitoring/IndicatorsTable";
import IndicatorDrawer from "@/components/monitoring/IndicatorDrawer";
import TasksTab from "@/components/monitoring/TasksTab";
import UpdatesDue from "@/components/monitoring/UpdatesDue";

const PROJECT_TABS = [["project", "Project view"], ["indicators", "Indicators"], ["tasks", "Tasks"], ["updates", "Updates due"]];

const download = async (url, params, name) => {
  try {
    const r = await api.get(url, { params, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = name; a.click();
  } catch { toast.error("Export failed"); }
};

function Filters({ projects, f, setF, owners, showOwner }) {
  return (
    <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b -mx-6 lg:-mx-8 px-6 lg:px-8 py-3 flex flex-wrap items-end gap-3" data-testid="mon-filters">
      <div><div className="text-[11px] text-slate-500">Project</div>
        <Select value={f.pid} onValueChange={(v) => setF({ ...f, pid: v })}><SelectTrigger className="w-64" data-testid="mon-project"><SelectValue placeholder="Pick a project" /></SelectTrigger>
          <SelectContent>{projects.length > 1 && <SelectItem value="all">All projects</SelectItem>}{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_code} · {p.name}</SelectItem>)}</SelectContent></Select></div>
      <div><div className="text-[11px] text-slate-500">FY</div>
        <Select value={f.fy} onValueChange={(v) => { const n = thisMonth(); setF({ ...f, fy: v, month: n < fyStart(v) ? fyStart(v) : n > fyEnd(v) ? fyEnd(v) : n }); }}>
          <SelectTrigger className="w-36" data-testid="mon-fy"><SelectValue /></SelectTrigger><SelectContent>{fyOptions().map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
      <div><div className="text-[11px] text-slate-500">As of month</div>
        <Input type="month" className="w-40" value={f.month} onChange={(e) => e.target.value && setF({ ...f, month: e.target.value, fy: fyOf(e.target.value) })} data-testid="mon-month" /></div>
      {showOwner && <div><div className="text-[11px] text-slate-500">Owner</div>
        <Select value={f.owner} onValueChange={(v) => setF({ ...f, owner: v })}><SelectTrigger className="w-48" data-testid="mon-owner"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All owners</SelectItem>{owners.map(([id, n]) => <SelectItem key={id} value={id}>{n}</SelectItem>)}</SelectContent></Select></div>}
    </div>
  );
}

export default function Monitoring() {
  const qs = new URLSearchParams(window.location.search);
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ pid: qs.get("project") || localStorage.getItem("mon_last") || "", fy: fyOf(thisMonth()), month: thisMonth(), owner: "all" });
  const [tab, setTab] = useState(qs.get("tab") || "project");
  const [data, setData] = useState(null);
  const [drawer, setDrawer] = useState(null);
  useEffect(() => {
    api.get("/projects").then(r => {
      const ps = (r.data || []).filter(p => p.project_code && !["Closed", "Archived"].includes(p.status)).sort((a, b) => a.project_code.localeCompare(b.project_code));
      setProjects(ps);
      setF(cur => ({ ...cur, pid: cur.pid === "all" && ps.length > 1 ? "all" : ps.some(p => p.id === cur.pid) ? cur.pid : ps.length > 1 ? "all" : ps[0]?.id || "" }));
    }).catch(() => setProjects([]));
  }, []);
  useEffect(() => {
    if (!f.pid) return;
    localStorage.setItem("mon_last", f.pid);
    setData(null);
    const req = f.pid === "all" ? api.get("/mel/dashboard", { params: { project_id: "all", month: f.month } }) : api.get(`/mel/monitoring/project/${f.pid}`, { params: { month: f.month } });
    req.then(r => setData(r.data)).catch(e => toast.error(e.response?.data?.detail || "Could not load"));
  }, [f.pid, f.month]);
  const all = f.pid === "all";
  const cur = all ? "portfolio" : tab;
  const owners = useMemo(() => {
    const m = new Map();
    const arr = (x) => (Array.isArray(x) ? x : []);
    arr(data?.indicators).forEach(i => i.owner_id && m.set(i.owner_id, i.owner_name));
    arr(data?.tasks).forEach(t => t.assignee_id && m.set(t.assignee_id, t.owner_name));
    return [...m.entries()].filter(([, n]) => n);
  }, [data]);
  const name = all ? "portfolio" : (projects.find(p => p.id === f.pid)?.project_code || "project");
  const tabs = all ? [["portfolio", "Portfolio"]] : PROJECT_TABS;
  return (
    <div className="p-6 lg:p-8 pt-0 lg:pt-0" data-testid="monitoring-page">
      <div className="pt-6 lg:pt-8 pb-3 flex flex-wrap items-end justify-between gap-3">
        <div><div className="text-xs uppercase tracking-widest text-slate-500">Projects</div><h1 className="font-display text-3xl font-extrabold">Project Monitoring</h1></div>
        {data && <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => download("/mel/dashboard/export.xlsx", { project_id: f.pid, month: f.month, tab: cur }, `${name}_${f.month}.xlsx`)} data-testid="mon-export-xlsx"><Download className="w-3.5 h-3.5 mr-1" />Excel</Button>
          <Button size="sm" variant="outline" onClick={() => download("/mel/dashboard/export.pdf", { project_id: f.pid, month: f.month, tab: cur }, `${name}_${f.month}.pdf`)} data-testid="mon-export-pdf"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
        </div>}
      </div>
      <Filters projects={projects} f={f} setF={setF} owners={owners} showOwner={!all && (tab === "indicators" || tab === "tasks")} />
      <div className="mt-4 mb-4">
        <div className="hidden md:block"><Tabs value={cur} onValueChange={setTab}><TabsList>{tabs.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`mon-tab-${k}`}>{l}</TabsTrigger>)}</TabsList></Tabs></div>
        <div className="md:hidden"><Select value={cur} onValueChange={setTab}><SelectTrigger data-testid="mon-tab-select"><SelectValue /></SelectTrigger>
          <SelectContent>{tabs.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
      </div>
      {!f.pid && projects.length === 0 && <div className="text-sm text-slate-500" data-testid="mon-no-projects">You are not on any project yet.</div>}
      {cur === "portfolio" && <PortfolioTab data={data?.cards ? data : null} onOpen={(id) => { setF({ ...f, pid: id }); setTab("project"); }} />}
      {cur === "project" && <ProjectView data={data?.card ? data : null} onOpenIndicator={setDrawer} />}
      {cur === "indicators" && <IndicatorsTable data={data?.card ? data : null} owner={f.owner} onOpen={setDrawer} />}
      {cur === "tasks" && <TasksTab data={data?.card ? data : null} owner={f.owner} month={f.month} />}
      {cur === "updates" && <UpdatesDue data={data?.card ? data : null} owner={f.owner} />}
      <IndicatorDrawer ind={drawer} fy={f.fy} canEdit={data?.can_edit} onClose={() => setDrawer(null)} />
    </div>
  );
}
